import { z } from "zod";

import { ClinicCallReason } from "@/packages/shared/src/models/clinic-call-reason";
import { DocumentForwardingReason } from "@/packages/shared/src/models/document-forwarding-reason";
import { STATE_NAMES } from "@/src/lib/us-states";
import { addressSchema } from "@/src/models/address";
import {
  faxAddresseeSchema,
  isAdwSchema,
  nullableSignaturePageNumberSchema,
  simpleDateSchema,
} from "@/src/models/common-schema";
import {
  CarePlanFollowUpAttempt,
  DmeOrderType,
  ProcedureCardiacType,
  ProcedureDeviceType,
  ProgressNoteDocumentType,
} from "@/src/models/constants/document-types";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import { ImagingType } from "@/src/models/constants/imaging-types";
import { LabSpecimenType } from "@/src/models/constants/lab-types";
import { patientSupplementalBlockFieldsSchema } from "@/src/models/constants/patient-supplemental";
import {
  ABNORMAL_RESULT_PROMPT,
  FOLLOW_UP_REQUIRED_PROMPT,
  BODY_PARTS_PROMPT,
  BODY_PART_LATERALITY_PROMPT,
  IMPRESSION_PROMPT,
  IS_NOT_THEIR_PATIENT_PROMPT,
  PROGRESS_NOTE_ENCOUNTER_TYPE_PROMPT,
  RESPONDS_TO_REFERRAL_PROMPT,
  SCHEDULING_PRIORITY_PROMPT,
  SIGNATURE_REQUIRED_PROMPT,
  STUDY_TYPE_PROMPT,
} from "@/src/models/constants/prompt";
import { RECORD_REQUEST_SOURCE_PROMPT, RecordRequestSource } from "@/src/models/constants/record-request-source";
import { SERVICE_LINE_PROMPT, ServiceLine } from "@/src/models/constants/service-lines";
import { Specialty } from "@/src/models/constants/specialty";
import { coverageSchema } from "@/src/models/coverage";
import { ClaimType, DisabilityDurationType } from "@/src/models/documents/admin/admin";
import { imagingStudiesSchema } from "@/src/models/documents/imaging/imaging-results";
import { LabResultsValue } from "@/src/models/documents/lab-results/lab-results";
import { medicalHistoryType } from "@/src/models/documents/medical-history/medical-history";
import {
  LegalStatute,
  RequestingLocation,
  SubpoenaPurpose,
} from "@/src/models/documents/medical-records-request/medical-record-request";
import {
  PriorAuthorizationRequestOrResponse,
  PriorAuthorizationStatus,
  PriorAuthorizationType,
  commonProcedureTerminologySchema,
} from "@/src/models/documents/prior-authorization/prior-authorization";
import {
  ProgressNoteFlowSheetType,
  ProgressNoteType,
  SpecialtyFolder,
} from "@/src/models/documents/progress-note/progress-note";
import {
  diagnosisListSchema,
  referralCoreMinimalSchema,
  ReferralResponseType,
  SchedulingPriority,
} from "@/src/models/documents/referral";
import {
  patientBlockMetadataSchema,
  practitionerBlockMetadataSchema,
  locationBlockMetadataSchema,
  coverageBlockMetadataSchema,
  referralInformationBlockMetadataSchema,
  documentInformationBlockMetadataSchema,
  priorAuthorizationBlockMetadataSchema,
  progressNoteBlockMetadataSchema,
  imagingResultsBlockMetadataSchema,
  labResultsBlockMetadataSchema,
  adminBlockMetadataSchema,
  continuityOfCareBlockMetadataSchema,
  medicalHistoryBlockMetadataSchema,
  medicalRecordRequestBlockMetadataSchema,
  notificationBlockMetadataSchema,
  pharmacyRelatedBlockMetadataSchema,
  emergencySummaryBlockMetadataSchema,
  referralResponseBlockMetadataSchema,
  carePlanBlockMetadataSchema,
  dmeBlockMetadataSchema,
  clearanceRequestBlockMetadataSchema,
  consentBlockMetadataSchema,
  financialBlockMetadataSchema,
  idCardBlockMetadataSchema,
  insuranceBlockMetadataSchema,
  intakeBlockMetadataSchema,
  patientSupplementalBlockMetadataSchema,
  legalBlockMetadataSchema,
  operationNoteBlockMetadataSchema,
  procedureBlockMetadataSchema,
} from "@/src/models/intake/blocks/block-metadata";
import { BlockKey, BlockType } from "@/src/models/intake/blocks/block-types";
import { locationSchema } from "@/src/models/location";
import { resultSchema } from "@/src/models/npi-registry";
import { patientSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

// Extend schemas with id and displayId
export const blockPatientSchema = patientSchema.extend({
  displayId: z.string().nullable(),
  id: z.string().uuid().nullable(),
});
export const blockPractitionerSchema = practitionerSchema.extend({
  displayId: z.string().nullable(),
  id: z.string().uuid().nullable(),
  npiRegistryEntry: resultSchema.nullable(),
  npiNumber: z.string().nullable(),
  isPcp: z.boolean().nullable().optional(),
});
export type BlockPractitioner = z.infer<typeof blockPractitionerSchema>;
export const blockLocationSchema = locationSchema.extend({
  displayId: z.string().nullable(),
  id: z.string().uuid().nullable(),
  placeId: z.string().nullable(),
});
export const blockCoverageSchema = z.intersection(coverageSchema, z.object({ id: z.string().uuid().nullable() }));

// Match result type for entity matching - Represents a potential match with confidence score
export const matchResultSchema = <T extends z.ZodTypeAny>(entitySchema: T) =>
  z.object({
    entity: entitySchema,
    confidence: z.number().min(0).max(1).describe("Matching confidence score from 0-1"),
  });

export type MatchResult<T extends z.ZodTypeAny> = z.infer<ReturnType<typeof matchResultSchema<T>>>;

/**
 * Base block schema with common fields
 */
const baseBlockSchema = z.object({
  entityMatchAutomationCritical: z.boolean().optional(),
  fieldAutomationCriticality: z.record(z.string(), z.boolean()).optional(),
  // A percentage of blocks are selected for manual review, which will be indicated in the UI.
  // This is determined once, at block-generation time.
  selectedForManualReview: z.boolean().nullable().default(false),
});

// Patient
export const patientBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PATIENT),
  documentExtractedFields: blockPatientSchema,
  initialReviewedFields: blockPatientSchema.nullable(),
  reviewedFields: blockPatientSchema.nullable(),
  matchResults: z.array(matchResultSchema(blockPatientSchema)).default([]),
  metadata: patientBlockMetadataSchema.nullable(),
});
export interface PatientBlock extends z.infer<typeof patientBlockSchema> {}
export type PatientMatchResult = PatientBlock["matchResults"][number];

// Coverage
export const coverageBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.COVERAGE),
  documentExtractedFields: blockCoverageSchema,
  initialReviewedFields: blockCoverageSchema.nullable(),
  reviewedFields: blockCoverageSchema.nullable(),
  matchResults: z.array(matchResultSchema(blockCoverageSchema)).default([]),
  metadata: coverageBlockMetadataSchema.nullable(),
});
export interface CoverageBlock extends z.infer<typeof coverageBlockSchema> {}
export type CoverageMatchResult = CoverageBlock["matchResults"][number];

// Provider
export const providerBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PROVIDER),
  documentExtractedFields: blockPractitionerSchema,
  initialReviewedFields: blockPractitionerSchema.nullable(),
  reviewedFields: blockPractitionerSchema.nullable(),
  matchResults: z.array(matchResultSchema(blockPractitionerSchema)).default([]),
  metadata: practitionerBlockMetadataSchema.nullable(),
});
export interface ProviderBlock extends z.infer<typeof providerBlockSchema> {}
export type ProviderMatchResult = ProviderBlock["matchResults"][number];

// Location
export const locationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.LOCATION),
  isDefaultFallback: z.boolean().optional(),
  documentExtractedFields: blockLocationSchema,
  initialReviewedFields: blockLocationSchema.nullable(),
  reviewedFields: blockLocationSchema.nullable(),
  matchResults: z
    .array(matchResultSchema(blockLocationSchema).extend({ isDefaultFallback: z.boolean().optional() }))
    .default([]),
  metadata: locationBlockMetadataSchema.nullable(),
});
export interface LocationBlock extends z.infer<typeof locationBlockSchema> {}
export type LocationMatchResult = LocationBlock["matchResults"][number];

// Referral information
export type ReferralInformationFields = z.infer<typeof referralCoreMinimalSchema>;
export const referralInformationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.REFERRAL_INFORMATION),
  documentExtractedFields: z.lazy(() => referralCoreMinimalSchema),
  initialReviewedFields: z.lazy(() => referralCoreMinimalSchema).nullable(),
  reviewedFields: z.lazy(() => referralCoreMinimalSchema).nullable(),
  selectedForManualReview: z.boolean().nullable().default(false),
  metadata: referralInformationBlockMetadataSchema.nullable(),
});
export interface ReferralInformationBlock extends z.infer<typeof referralInformationBlockSchema> {}

export const appointmentStatusSchema = z
  .string()
  .nullable()
  .describe(
    "Status of an already-scheduled or already-attended appointment referenced in the document — e.g. 'Scheduled', 'Attended', 'Seen'. Informational only; does not drive any routing/authorization logic."
  );

// Document Information is a generic block which encompasses all information that doesn't map to a defined entity
export const documentInformationFieldSchema = z.object({
  isAdw: isAdwSchema,
  ahi: z.string().nullable(),
  appointmentStatus: appointmentStatusSchema,
  authorizationNumber: z.string().nullable(),
  authorizationStatus: z.nativeEnum(PriorAuthorizationStatus).nullable(),
  availableForDownload: z
    .boolean()
    .nullable()
    .describe(
      "Whether the document indicates the requested medical records are available electronically — for example, when an access code, download link, or instructions to retrieve records via a portal are present. False when the document attaches the records directly or otherwise does not require electronic retrieval."
    ),
  bodyPart: z.string().nullable(),
  bodyPartLaterality: z.string().nullable(),
  cannotServicePatient: z.boolean().nullable(),
  cde: z.boolean().nullable(),
  isAddCptRequest: z.boolean().nullable(),
  isDxChangeRequest: z.boolean().nullable(),
  authExpired: z.boolean().nullable(),
  isPpoPlan: z
    .boolean()
    .nullable()
    .describe(
      "Whether the patient's primary insurance coverage named on this prior authorization is a PPO (Preferred Provider Organization) plan, as opposed to an HMO or IPA-managed plan. True when the plan type, payer name, or IPA on the document identifies it as PPO. False when it identifies an HMO/IPA-managed plan. Null if the plan type cannot be determined."
    ),
  isSurgicalAuthorization: z
    .boolean()
    .nullable()
    .describe(
      "Whether this prior authorization is specifically for a surgical procedure/operation, as opposed to an office visit, imaging study, or other non-surgical service. True when the authorized service is a surgery."
    ),
  isVaDocument: z
    .boolean()
    .nullable()
    .describe(
      "Whether this document was sent by the U.S. Department of Veterans Affairs itself — i.e. the VA is the ORIGIN of the document. True only when the sender is explicitly identified as the VA: VA / Veterans Affairs / Veterans Health Administration (VHA) letterhead, a named VA Medical Center (VAMC), VA hospital, or VA outpatient clinic in the sender/from block, or a va.gov address or fax header. False when a third-party administrator of VA community care (for example TriWest or Optum) is the sender, even if the document references the VA, veterans, or the Community Care Network. False when the VA appears only as the patient's insurance or coverage (VA, CHAMPVA, TRICARE, Veterans Choice) rather than as the party that sent the document."
    ),
  isEarlySteps: z.boolean().nullable(),
  isInsuranceFollowUp: z.boolean().nullable(),
  isInsuranceAuditRequest: z
    .boolean()
    .nullable()
    .describe(
      "True when the document is a payer/auditor request for chart documentation or records review (e.g. an insurance audit or records request from an insurer), rather than routine insurance correspondence such as a policy update or claim notice."
    ),
  isOtherReferralReason: z.boolean().nullable(),
  recordsRequested: z.boolean().nullable(),
  containsMultiplePatients: z.boolean().nullable(),
  claimType: z.nativeEnum(ClaimType).nullable(),
  clinicName: z.string().nullable(),
  companyName: z.string().nullable(),
  companyNameOther: z
    .string()
    .nullable()
    .describe("Company name written in free text when the company is not one of the companyName options."),
  containsAdmissionLabel: z.boolean().nullable(),
  containsCptCode: z.boolean().nullable(),
  dateOfDocument: simpleDateSchema.nullable(),
  collectionDate: simpleDateSchema.nullable(),
  dateOfEvent: simpleDateSchema.nullable(),
  dateOfProgressNote: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  dateSigned: simpleDateSchema.nullable(),
  diagnosisList: diagnosisListSchema.nullable(),
  diagnosisNameList: z.array(z.string()).nullable(),
  primaryDiagnosis: z
    .string()
    .nullable()
    .describe(
      "The single primary medical reason the patient is being seen — the underlying condition or diagnosis that explains the visit, not a symptom. For example, if diagnosisList captures nausea, headache, and dizziness, primaryDiagnosis should be the condition causing them (e.g., 'Concussion'). Null if the document does not clearly indicate one overriding diagnosis."
    ),
  commonProcedureTerminologyList: z
    .array(commonProcedureTerminologySchema)
    .nullable()
    .describe(
      "CPT/HCPCS procedure or service codes referenced in the document: one entry per code with the code, full text description, and modifier when shown. Use null if no billable procedure codes appear."
    ),
  dischargePlan: z.string().nullable(),
  disabilityDurationType: z.nativeEnum(DisabilityDurationType).nullable(),
  dispositionReason: z.string().nullable(),
  documentType: z.string().nullable(),
  type: z.string().nullable(),
  requestOrResponse: z.nativeEnum(PriorAuthorizationRequestOrResponse).nullable(),
  dmeType: z.string().nullable(),
  dmeOrderType: z.nativeEnum(DmeOrderType).nullable(),
  procedureCardiacType: z.nativeEnum(ProcedureCardiacType).nullable(),
  procedureDeviceType: z.nativeEnum(ProcedureDeviceType).nullable(),
  equipmentOrSupply: z.string().nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  recordRequestSource: z.nativeEnum(RecordRequestSource).nullable().describe(RECORD_REQUEST_SOURCE_PROMPT),
  flowSheetType: z.nativeEnum(ProgressNoteFlowSheetType).nullable(),
  hospitalAbbrev: z.string().nullable(),
  imagingType: z.string().nullable(),
  impression: z.string().nullable().describe(IMPRESSION_PROMPT),
  insuranceCompanyName: z.string().nullable(),
  intakeNote: z.string().nullable(),
  isDischarge: z.boolean().nullable(),
  isFromMedicalExaminer: z.boolean().nullable(),
  isIcd: z.boolean().nullable(),
  isLegal: z
    .boolean()
    .nullable()
    .describe("True when the medical records request is legal in nature; false otherwise."),
  isNewborn: z.boolean().nullable(),
  isOverread: z.boolean().nullable(),
  followUpAttempt: z.nativeEnum(CarePlanFollowUpAttempt).nullable(),
  isScored: z.boolean().nullable(),
  isSigned: z.boolean().nullable(),
  isSkilledNursingOrHomeHealth: z.boolean().nullable(),
  isStockings: z.boolean().nullable(),
  isSummaryOfCare: z.boolean().nullable(),
  labTypesAndResults: z
    .array(
      z.object({
        typeOfLab: z.string().nullable(),
        labResult: z.nativeEnum(LabResultsValue).nullable(),
      })
    )
    .nullable(),
  legalStatute: z.nativeEnum(LegalStatute).nullable(),
  listOfNameOfLabs: z.array(z.string()).nullable(),
  locationName: z.string().nullable(),
  medicationName: z.string().nullable(),
  supplyDuration: z.string().nullable(),
  medications: z.array(z.string()).nullable(),
  medicalHistoryType: z.nativeEnum(medicalHistoryType).nullable(),
  nameOfDocument: z.string().nullable(),
  nameOfIllness: z.string().nullable(),
  nameOfLab: z.string().nullable(),
  isOperation: z.boolean().nullable(),
  orderNumber: z.string().nullable(),
  transferReason: z.string().nullable(),
  incompleteRoi: z.string().nullable(),
  lwcImmunization: z.boolean().nullable(),
  patientLogType: z.string().nullable(),
  usStateName: z.nativeEnum(STATE_NAMES).nullable(),
  procedure: z.string().nullable(),
  progressNoteType: z.nativeEnum(ProgressNoteType).nullable(),
  clinicCallReason: z.nativeEnum(ClinicCallReason).nullable(),
  forwardingReason: z
    .nativeEnum(DocumentForwardingReason)
    .nullable()
    .describe(
      'Why this document is being forwarded instead of filed to a patient chart. Use "No patient information" when the document contains no identifiable patient, or "General document to the clinic" when it concerns the clinic rather than a specific patient.'
    ),
  reasonForReferral: z.string().nullable(),
  reasonForVisit: z.string().nullable(),
  recommendation: z.string().nullable(),
  referralResponse: z.nativeEnum(ReferralResponseType).nullable(),
  requestingLocation: z.nativeEnum(RequestingLocation).nullable(),
  result: z.nativeEnum(LabResultsValue).nullable(),
  resultsPresent: z.boolean().nullable(),
  respondsToReferral: z.boolean().nullable().describe(RESPONDS_TO_REFERRAL_PROMPT),
  schedulingPriority: z.nativeEnum(SchedulingPriority).nullable().describe(SCHEDULING_PRIORITY_PROMPT),
  senderName: z.string().nullable(),
  faxAddressee: faxAddresseeSchema,
  signaturePageNumber: nullableSignaturePageNumberSchema,
  signatureRequired: z.boolean().nullable().describe(SIGNATURE_REQUIRED_PROMPT),
  significantFindings: z.boolean().nullable(),
  abnormal: z.boolean().nullable().describe(ABNORMAL_RESULT_PROMPT),
  followUpRequired: z.boolean().nullable().describe(FOLLOW_UP_REQUIRED_PROMPT),
  specialty: z.nativeEnum(Specialty).nullable(),
  specialties: z.array(z.nativeEnum(Specialty)).nullable(),
  specialtyFolder: z.nativeEnum(SpecialtyFolder).nullable(),
  specialtyOrFacilityName: z.string().nullable(),
  studyType: z.string().nullable().describe(STUDY_TYPE_PROMPT),
  subpoenaPurpose: z.nativeEnum(SubpoenaPurpose).nullable(),
  summaryStatements: z.string().nullable(),
  surgeryName: z.string().nullable(),
  testNotPerformed: z.boolean().nullable(),
  specimenCollected: z.boolean().nullable(),
  typeOfLab: z.string().nullable(),
  vaccineType: z.string().nullable(),
  hasMultipleDates: z
    .boolean()
    .nullable()
    .describe(
      "Whether the document contains records from multiple different dates or encounters. If the document spans multiple visit dates (e.g., a chart with entries from different days), set to true. If it covers a single encounter or date, set to false."
    ),
});
export type DocumentInformationFields = z.infer<typeof documentInformationFieldSchema>;
export const documentInformationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.DOCUMENT_INFORMATION),
  documentExtractedFields: z.lazy(() => documentInformationFieldSchema),
  initialReviewedFields: z.lazy(() => documentInformationFieldSchema).nullable(),
  reviewedFields: z.lazy(() => documentInformationFieldSchema).nullable(),
  metadata: documentInformationBlockMetadataSchema.nullable(),
});
export interface DocumentInformationBlock extends z.infer<typeof documentInformationBlockSchema> {}

// Prior authorization fields schema
export const priorAuthorizationFieldSchema = z.object({
  isAdw: isAdwSchema,
  requestId: z.string().nullable(),
  authorizationNumber: z
    .string()
    .nullable()
    .describe(
      `The final approval number/certification code assigned to this authorization upon approval.
      Look for labels like "Authorization", "Auth #", "Auth Number", "Pre-Auth", "Prior Auth",
      "Certification Number", "Cert #". Return only the alphanumeric identifier (may include hyphens).
      Do not confuse with Policy ID, Group Number, Member ID, Subscriber ID, or request/tracking IDs.`
    ),
  authorizationType: z.nativeEnum(PriorAuthorizationType).nullable(),
  authorizationStatus: z.nativeEnum(PriorAuthorizationStatus).nullable(),
  authorizationFor: z.string().nullable(),
  visitCategory: z.string().nullable(),
  commonProcedureTerminologyList: z.array(commonProcedureTerminologySchema).nullable(),
  startDate: simpleDateSchema.nullable(),
  endDate: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  dateOfDocument: simpleDateSchema.nullable(),
  dateOfEvent: simpleDateSchema.nullable(),
  medicationName: z.string().nullable(),
  supplyDuration: z.string().nullable(),
  numberOfServices: z.number().nullable(),
  specialty: z.nativeEnum(Specialty).nullable(),
  isContinuationOfCarePriorAuth: z
    .boolean()
    .nullable()
    .describe(
      "True when this authorization request continues an existing course of care for the patient rather than " +
        "starting a new episode — e.g. it requests an extension of an existing authorization, additional or " +
        `extra visits, or otherwise indicates ongoing treatment. Look for language like "extension", ` +
        `"additional visits", "extra visits", "continuation of care", "continued care", "ongoing therapy", ` +
        `or a request to add visits to an existing referral/authorization. False or null otherwise.`
    ),
  isPostOpPriorAuth: z
    .boolean()
    .nullable()
    .describe(
      "True when this authorization request is for post-operative / post-surgical care — i.e. the diagnosis or " +
        `reason for the request references a recent or planned surgery. Look for language like "post-op", ` +
        `"post-operative", "surgical", "P/O", "surgery", or a specific surgical procedure such as total knee ` +
        "replacement (TKR), total hip replacement, ACL reconstruction, rotator cuff repair, spinal fusion or " +
        "decompression, etc. False or null otherwise."
    ),
});

// Prior authorization block schema
export type PriorAuthorizationFields = z.infer<typeof priorAuthorizationFieldSchema>;
export const priorAuthorizationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PRIOR_AUTHORIZATION),
  documentExtractedFields: z.lazy(() => priorAuthorizationFieldSchema),
  initialReviewedFields: z.lazy(() => priorAuthorizationFieldSchema).nullable(),
  reviewedFields: z.lazy(() => priorAuthorizationFieldSchema).nullable(),
  metadata: priorAuthorizationBlockMetadataSchema.nullable(),
});
export interface PriorAuthorizationBlock extends z.infer<typeof priorAuthorizationBlockSchema> {}

// Progress note fields schema
export const progressNoteFieldSchema = z.object({
  isAdw: isAdwSchema,
  dateOfProgressNote: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  type: z.nativeEnum(ProgressNoteDocumentType).nullable(),
  progressNoteType: z.nativeEnum(ProgressNoteType).nullable(),
  encounterType: z.string().nullable().describe(PROGRESS_NOTE_ENCOUNTER_TYPE_PROMPT),
  dateOfEvent: simpleDateSchema.nullable(),
  reasonForVisit: z.string().nullable(),
  reasonForReferral: z.string().nullable(),
  diagnosisList: diagnosisListSchema.nullable(),
  bodyPart: z.string().nullable(),
  specialty: z.nativeEnum(Specialty).nullable(),
  specialtyFolder: z.nativeEnum(SpecialtyFolder).nullable(),
  orderNumber: z.string().nullable(),
  surgeryName: z.string().nullable(),
  hospitalAbbrev: z.string().nullable(),
  signatureRequired: z.boolean().nullable().describe(SIGNATURE_REQUIRED_PROMPT),
  flowSheetType: z.nativeEnum(ProgressNoteFlowSheetType).nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  isSummaryOfCare: z.boolean().nullable(),
  serviceLine: z.nativeEnum(ServiceLine).nullable().describe(SERVICE_LINE_PROMPT),
  locationName: z.string().nullable(),
  isOperation: z.boolean().nullable(),
  isConsult: z.boolean().nullable(),
  isTelemedicine: z.boolean().nullable(),
  isDischarge: z.boolean().nullable(),
  isIfsp: z.boolean().nullable(),
  clinicName: z.string().nullable(),
  cde: z.boolean().nullable(),
});

// Progress note block schema
export type ProgressNoteFields = z.infer<typeof progressNoteFieldSchema>;
export const progressNoteBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PROGRESS_NOTE),
  documentExtractedFields: z.lazy(() => progressNoteFieldSchema),
  initialReviewedFields: z.lazy(() => progressNoteFieldSchema).nullable(),
  reviewedFields: z.lazy(() => progressNoteFieldSchema).nullable(),
  metadata: progressNoteBlockMetadataSchema.nullable(),
});
export interface ProgressNoteBlock extends z.infer<typeof progressNoteBlockSchema> {}

// Imaging results fields schema
export const imagingResultsFieldSchema = z.object({
  isAdw: isAdwSchema,
  dateOfImagingResults: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  dateOfEvent: simpleDateSchema.nullable(),
  // Imaging results have no documentInformation block to carry the addressee, so it lives here for
  // groups that route imaging to the nurse the fax was addressed to.
  faxAddressee: faxAddresseeSchema,
  imagingType: z.nativeEnum(ImagingType).nullable(),
  imagingTypes: z.array(z.nativeEnum(ImagingType)).nullable(),
  imagingStudies: imagingStudiesSchema,
  studyType: z.string().nullable(),
  bodyPart: z.string().nullable(),
  bodyParts: z.array(z.string()).nullable().describe(BODY_PARTS_PROMPT),
  bodyPartLaterality: z.string().nullable().describe(BODY_PART_LATERALITY_PROMPT),
  procedure: z.string().nullable(),
  needsSignature: z.boolean().nullable(),
  needsClarification: z.boolean().nullable(),
  significantFindings: z.boolean().nullable(),
  isPretesting: z.boolean().nullable(),
  isOverread: z
    .boolean()
    .nullable()
    .describe(
      "True when this report is a second opinion — a subsequent expert review and interpretation of a study previously read by another radiologist or provider — rather than the original/primary read."
    ),
});

// Imaging results block schema
export type ImagingResultsFields = z.infer<typeof imagingResultsFieldSchema>;
export const imagingResultsBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.IMAGING_RESULTS),
  documentExtractedFields: z.lazy(() => imagingResultsFieldSchema),
  initialReviewedFields: z.lazy(() => imagingResultsFieldSchema).nullable(),
  reviewedFields: z.lazy(() => imagingResultsFieldSchema).nullable(),
  metadata: imagingResultsBlockMetadataSchema.nullable(),
});
export interface ImagingResultsBlock extends z.infer<typeof imagingResultsBlockSchema> {}

// Lab results fields schema
export const labResultsFieldSchema = z.object({
  isAdw: isAdwSchema,
  dateOfLabResults: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  dateOfEvent: simpleDateSchema.nullable(),
  collectionDate: simpleDateSchema.nullable(),
  nameOfLab: z.string().nullable(),
  listOfNameOfLabs: z.array(z.string()).nullable(),
  typeOfLab: z.string().nullable(),
  labResult: z.nativeEnum(LabResultsValue).nullable(),
  labTypesAndResults: z
    .array(
      z.object({
        typeOfLab: z.string().nullable(),
        labResult: z.nativeEnum(LabResultsValue).nullable(),
      })
    )
    .nullable(),
  bodyPart: z.string().nullable(),
  needsSignature: z.boolean().nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  testNotPerformed: z.boolean().nullable(),
  specimenCollected: z.boolean().nullable(),
  specimenType: z.nativeEnum(LabSpecimenType).nullable(),
  resultsPresent: z.boolean().nullable(),
  isOrderedBySpecialist: z.boolean().nullable(),
  isNewborn: z.boolean().nullable(),
  isPretesting: z.boolean().nullable(),
  isLabAddOn: z.boolean().nullable(),
});

// Lab results block schema
export type LabResultsFields = z.infer<typeof labResultsFieldSchema>;
export const labResultsBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.LAB_RESULTS),
  documentExtractedFields: z.lazy(() => labResultsFieldSchema),
  initialReviewedFields: z.lazy(() => labResultsFieldSchema).nullable(),
  reviewedFields: z.lazy(() => labResultsFieldSchema).nullable(),
  metadata: labResultsBlockMetadataSchema.nullable(),
});
export interface LabResultsBlock extends z.infer<typeof labResultsBlockSchema> {}

// Admin fields schema
export const adminFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  authorizationStatus: true,
  bodyPart: true,
  claimType: true,
  companyName: true,
  dateOfDocument: true,
  dateOfEvent: true,
  dateReceived: true,
  disabilityDurationType: true,
  documentType: true,
  equipmentOrSupply: true,
  facilityType: true,
  isSigned: true,
  nameOfDocument: true,
  nameOfIllness: true,
  schedulingPriority: true,
  signaturePageNumber: true,
  signatureRequired: true,
  specialtyOrFacilityName: true,
});

export type AdminFields = z.infer<typeof adminFieldSchema>;
export const adminBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.ADMIN),
  documentExtractedFields: z.lazy(() => adminFieldSchema),
  initialReviewedFields: z.lazy(() => adminFieldSchema).nullable(),
  reviewedFields: z.lazy(() => adminFieldSchema).nullable(),
  metadata: adminBlockMetadataSchema.nullable(),
});
export interface AdminBlock extends z.infer<typeof adminBlockSchema> {}

// Continuity of Care fields schema
export const continuityOfCareFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  authorizationStatus: true,
  companyName: true,
  dateOfDocument: true,
  dateOfEvent: true,
  dateReceived: true,
  diagnosisList: true,
  documentType: true,
  facilityType: true,
  hospitalAbbrev: true,
  orderNumber: true,
  schedulingPriority: true,
  signatureRequired: true,
  specialtyFolder: true,
});

export type ContinuityOfCareFields = z.infer<typeof continuityOfCareFieldSchema>;
export const continuityOfCareBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.CONTINUITY_OF_CARE),
  documentExtractedFields: z.lazy(() => continuityOfCareFieldSchema),
  initialReviewedFields: z.lazy(() => continuityOfCareFieldSchema).nullable(),
  reviewedFields: z.lazy(() => continuityOfCareFieldSchema).nullable(),
  metadata: continuityOfCareBlockMetadataSchema.nullable(),
});
export interface ContinuityOfCareBlock extends z.infer<typeof continuityOfCareBlockSchema> {}

// Medical History fields schema
export const medicalHistoryFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  availableForDownload: true,
  dateOfEvent: true,
  dateReceived: true,
  isSigned: true,
});

export type MedicalHistoryFields = z.infer<typeof medicalHistoryFieldSchema>;
export const medicalHistoryBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.MEDICAL_HISTORY),
  documentExtractedFields: z.lazy(() => medicalHistoryFieldSchema),
  initialReviewedFields: z.lazy(() => medicalHistoryFieldSchema).nullable(),
  reviewedFields: z.lazy(() => medicalHistoryFieldSchema).nullable(),
  metadata: medicalHistoryBlockMetadataSchema.nullable(),
});
export interface MedicalHistoryBlock extends z.infer<typeof medicalHistoryBlockSchema> {}

// Medical Record Request fields schema
export const medicalRecordRequestFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  dateOfEvent: true,
  dateReceived: true,
  incompleteRoi: true,
  isFromMedicalExaminer: true,
  legalStatute: true,
  lwcImmunization: true,
  requestingLocation: true,
  schedulingPriority: true,
  specialty: true,
  subpoenaPurpose: true,
  transferReason: true,
});

export type MedicalRecordRequestFields = z.infer<typeof medicalRecordRequestFieldSchema>;
export const medicalRecordRequestBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.MEDICAL_RECORD_REQUEST),
  documentExtractedFields: z.lazy(() => medicalRecordRequestFieldSchema),
  initialReviewedFields: z.lazy(() => medicalRecordRequestFieldSchema).nullable(),
  reviewedFields: z.lazy(() => medicalRecordRequestFieldSchema).nullable(),
  metadata: medicalRecordRequestBlockMetadataSchema.nullable(),
});
export interface MedicalRecordRequestBlock extends z.infer<typeof medicalRecordRequestBlockSchema> {}

// Notification fields schema
export const notificationEncounterTypeSchema = z.enum(["Admit", "Discharge"]);
export type NotificationEncounterType = z.infer<typeof notificationEncounterTypeSchema>;
export const notificationFieldSchema = documentInformationFieldSchema
  .pick({
    isAdw: true,
    dateOfDocument: true,
    dateOfEvent: true,
    facilityType: true,
    isNewborn: true,
    nameOfLab: true,
  })
  .extend({
    encounterType: notificationEncounterTypeSchema.nullable(),
  });

export type NotificationFields = z.infer<typeof notificationFieldSchema>;
export const notificationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.NOTIFICATION),
  documentExtractedFields: z.lazy(() => notificationFieldSchema),
  initialReviewedFields: z.lazy(() => notificationFieldSchema).nullable(),
  reviewedFields: z.lazy(() => notificationFieldSchema).nullable(),
  metadata: notificationBlockMetadataSchema.nullable(),
});
export interface NotificationBlock extends z.infer<typeof notificationBlockSchema> {}

// Pharmacy Related fields schema
export const pharmacyRelatedFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  dateOfDocument: true,
  dateOfEvent: true,
  dateReceived: true,
  medicationName: true,
  supplyDuration: true,
  medications: true,
});

export type PharmacyRelatedFields = z.infer<typeof pharmacyRelatedFieldSchema>;
export const pharmacyRelatedBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PHARMACY_RELATED),
  documentExtractedFields: z.lazy(() => pharmacyRelatedFieldSchema),
  initialReviewedFields: z.lazy(() => pharmacyRelatedFieldSchema).nullable(),
  reviewedFields: z.lazy(() => pharmacyRelatedFieldSchema).nullable(),
  metadata: pharmacyRelatedBlockMetadataSchema.nullable(),
});
export interface PharmacyRelatedBlock extends z.infer<typeof pharmacyRelatedBlockSchema> {}

// Emergency Summary fields schema
export const emergencySummaryFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  facilityType: true,
  containsAdmissionLabel: true,
  dispositionReason: true,
  isDischarge: true,
  isSummaryOfCare: true,
  isNewborn: true,
  specialty: true,
  diagnosisList: true,
  diagnosisNameList: true,
  primaryDiagnosis: true,
  locationName: true,
  dischargePlan: true,
});

export type EmergencySummaryFields = z.infer<typeof emergencySummaryFieldSchema>;
export const emergencySummaryBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.EMERGENCY_SUMMARY),
  documentExtractedFields: z.lazy(() => emergencySummaryFieldSchema),
  initialReviewedFields: z.lazy(() => emergencySummaryFieldSchema).nullable(),
  reviewedFields: z.lazy(() => emergencySummaryFieldSchema).nullable(),
  metadata: emergencySummaryBlockMetadataSchema.nullable(),
});
export interface EmergencySummaryBlock extends z.infer<typeof emergencySummaryBlockSchema> {}

// Referral Response fields schema
export const referralResponseFieldSchema = documentInformationFieldSchema
  .pick({
    isAdw: true,
    dateReceived: true,
    documentType: true,
    referralResponse: true,
  })
  .extend({
    dateOfReferral: simpleDateSchema.nullable(),
    dateOfEvent: simpleDateSchema.nullable(),
    reasonForReferral: z.string().nullable(),
    specialty: z.nativeEnum(Specialty).nullable(),
    clinicName: z.string().nullable(),
  });

export type ReferralResponseFields = z.infer<typeof referralResponseFieldSchema>;
export const referralResponseBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.REFERRAL_RESPONSE),
  documentExtractedFields: z.lazy(() => referralResponseFieldSchema),
  initialReviewedFields: z.lazy(() => referralResponseFieldSchema).nullable(),
  reviewedFields: z.lazy(() => referralResponseFieldSchema).nullable(),
  metadata: referralResponseBlockMetadataSchema.nullable(),
});
export interface ReferralResponseBlock extends z.infer<typeof referralResponseBlockSchema> {}

// Care Plan fields schema
export const carePlanFieldSchema = documentInformationFieldSchema
  .pick({
    clinicName: true,
    dateOfDocument: true,
    dateOfEvent: true,
    dateReceived: true,
    dateSigned: true,
    documentType: true,
    followUpAttempt: true,
    isSigned: true,
    signaturePageNumber: true,
    bodyPart: true,
    diagnosisList: true,
  })
  .extend({
    serviceLine: z.nativeEnum(ServiceLine).nullable().describe(SERVICE_LINE_PROMPT),
    isNotTheirPatient: z.boolean().nullable().describe(IS_NOT_THEIR_PATIENT_PROMPT),
  });

export type CarePlanFields = z.infer<typeof carePlanFieldSchema>;
export const carePlanBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.CARE_PLAN),
  documentExtractedFields: z.lazy(() => carePlanFieldSchema),
  initialReviewedFields: z.lazy(() => carePlanFieldSchema).nullable(),
  reviewedFields: z.lazy(() => carePlanFieldSchema).nullable(),
  metadata: carePlanBlockMetadataSchema.nullable(),
});
export interface CarePlanBlock extends z.infer<typeof carePlanBlockSchema> {}

// DME fields schema
export const dmeFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  authorizationStatus: true,
  dateOfDocument: true,
  dateOfEvent: true,
  dateReceived: true,
  dmeType: true,
});

export type DmeFields = z.infer<typeof dmeFieldSchema>;
export const dmeBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.DME),
  documentExtractedFields: z.lazy(() => dmeFieldSchema),
  initialReviewedFields: z.lazy(() => dmeFieldSchema).nullable(),
  reviewedFields: z.lazy(() => dmeFieldSchema).nullable(),
  metadata: dmeBlockMetadataSchema.nullable(),
});
export interface DmeBlock extends z.infer<typeof dmeBlockSchema> {}

// Clearance Request fields schema
export const clearanceRequestFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  clinicName: true,
  companyName: true,
  dateOfEvent: true,
  dateReceived: true,
  dateSigned: true,
  faxAddressee: true,
  schedulingPriority: true,
  signatureRequired: true,
  specialty: true,
  surgeryName: true,
});

export type ClearanceRequestFields = z.infer<typeof clearanceRequestFieldSchema>;
export const clearanceRequestBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.CLEARANCE_REQUEST),
  documentExtractedFields: z.lazy(() => clearanceRequestFieldSchema),
  initialReviewedFields: z.lazy(() => clearanceRequestFieldSchema).nullable(),
  reviewedFields: z.lazy(() => clearanceRequestFieldSchema).nullable(),
  metadata: clearanceRequestBlockMetadataSchema.nullable(),
});
export interface ClearanceRequestBlock extends z.infer<typeof clearanceRequestBlockSchema> {}

// Consent fields schema
export const consentFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  dateReceived: true,
  dateSigned: true,
  nameOfDocument: true,
});

export type ConsentFields = z.infer<typeof consentFieldSchema>;
export const consentBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.CONSENT),
  documentExtractedFields: z.lazy(() => consentFieldSchema),
  initialReviewedFields: z.lazy(() => consentFieldSchema).nullable(),
  reviewedFields: z.lazy(() => consentFieldSchema).nullable(),
  metadata: consentBlockMetadataSchema.nullable(),
});
export interface ConsentBlock extends z.infer<typeof consentBlockSchema> {}

// Financial fields schema
export const financialFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  authorizationStatus: true,
  companyName: true,
  dateReceived: true,
  documentType: true,
  signatureRequired: true,
});

export type FinancialFields = z.infer<typeof financialFieldSchema>;
export const financialBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.FINANCIAL),
  documentExtractedFields: z.lazy(() => financialFieldSchema),
  initialReviewedFields: z.lazy(() => financialFieldSchema).nullable(),
  reviewedFields: z.lazy(() => financialFieldSchema).nullable(),
  metadata: financialBlockMetadataSchema.nullable(),
});
export interface FinancialBlock extends z.infer<typeof financialBlockSchema> {}

// ID Card fields schema
export const idCardFieldSchema = documentInformationFieldSchema.pick({
  dateReceived: true,
});

export type IdCardFields = z.infer<typeof idCardFieldSchema>;
export const idCardBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.ID_CARD),
  documentExtractedFields: z.lazy(() => idCardFieldSchema),
  initialReviewedFields: z.lazy(() => idCardFieldSchema).nullable(),
  reviewedFields: z.lazy(() => idCardFieldSchema).nullable(),
  metadata: idCardBlockMetadataSchema.nullable(),
});
export interface IdCardBlock extends z.infer<typeof idCardBlockSchema> {}

// Insurance fields schema
export const insuranceFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  dateOfEvent: true,
  dateReceived: true,
  insuranceCompanyName: true,
  signatureRequired: true,
  isInsuranceAuditRequest: true,
  containsMultiplePatients: true,
});

export type InsuranceFields = z.infer<typeof insuranceFieldSchema>;
export const insuranceBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.INSURANCE),
  documentExtractedFields: z.lazy(() => insuranceFieldSchema),
  initialReviewedFields: z.lazy(() => insuranceFieldSchema).nullable(),
  reviewedFields: z.lazy(() => insuranceFieldSchema).nullable(),
  metadata: insuranceBlockMetadataSchema.nullable(),
});
export interface InsuranceBlock extends z.infer<typeof insuranceBlockSchema> {}

// Intake fields schema
export const intakeFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  dateOfEvent: true,
  dateReceived: true,
  intakeNote: true,
});

export type IntakeFields = z.infer<typeof intakeFieldSchema>;
export const intakeBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.INTAKE),
  documentExtractedFields: z.lazy(() => intakeFieldSchema),
  initialReviewedFields: z.lazy(() => intakeFieldSchema).nullable(),
  reviewedFields: z.lazy(() => intakeFieldSchema).nullable(),
  metadata: intakeBlockMetadataSchema.nullable(),
});
export interface IntakeBlock extends z.infer<typeof intakeBlockSchema> {}

// Patient supplemental fields schema
export const patientSupplementalFieldSchema = patientSupplementalBlockFieldsSchema;

export type PatientSupplementalFields = z.infer<typeof patientSupplementalFieldSchema>;
export const patientSupplementalBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PATIENT_SUPPLEMENTAL),
  documentExtractedFields: z.lazy(() => patientSupplementalFieldSchema),
  initialReviewedFields: z.lazy(() => patientSupplementalFieldSchema).nullable(),
  reviewedFields: z.lazy(() => patientSupplementalFieldSchema).nullable(),
  metadata: patientSupplementalBlockMetadataSchema.nullable(),
});
export interface PatientSupplementalBlock extends z.infer<typeof patientSupplementalBlockSchema> {}

// Legal fields schema
export const legalFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  dateOfEvent: true,
  dateReceived: true,
  dateSigned: true,
  documentType: true,
  signatureRequired: true,
});

export type LegalFields = z.infer<typeof legalFieldSchema>;
export const legalBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.LEGAL),
  documentExtractedFields: z.lazy(() => legalFieldSchema),
  initialReviewedFields: z.lazy(() => legalFieldSchema).nullable(),
  reviewedFields: z.lazy(() => legalFieldSchema).nullable(),
  metadata: legalBlockMetadataSchema.nullable(),
});
export interface LegalBlock extends z.infer<typeof legalBlockSchema> {}

// Operation Note fields schema
export const operationNoteFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  dateOfEvent: true,
  specialty: true,
});

export type OperationNoteFields = z.infer<typeof operationNoteFieldSchema>;
export const operationNoteBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.OPERATION_NOTE),
  documentExtractedFields: z.lazy(() => operationNoteFieldSchema),
  initialReviewedFields: z.lazy(() => operationNoteFieldSchema).nullable(),
  reviewedFields: z.lazy(() => operationNoteFieldSchema).nullable(),
  metadata: operationNoteBlockMetadataSchema.nullable(),
});
export interface OperationNoteBlock extends z.infer<typeof operationNoteBlockSchema> {}

// Procedure fields schema
export const procedureFieldSchema = documentInformationFieldSchema.pick({
  isAdw: true,
  ahi: true,
  dateOfEvent: true,
  dateReceived: true,
  facilityType: true,
  isIcd: true,
  isSigned: true,
  procedure: true,
  procedureCardiacType: true,
  procedureDeviceType: true,
  recommendation: true,
  studyType: true,
  summaryStatements: true,
});

export type ProcedureFields = z.infer<typeof procedureFieldSchema>;
export const procedureBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PROCEDURE),
  documentExtractedFields: z.lazy(() => procedureFieldSchema),
  initialReviewedFields: z.lazy(() => procedureFieldSchema).nullable(),
  reviewedFields: z.lazy(() => procedureFieldSchema).nullable(),
  metadata: procedureBlockMetadataSchema.nullable(),
});
export interface ProcedureBlock extends z.infer<typeof procedureBlockSchema> {}

export type Block = BlockMap[BlockKey];

// Typed paths
type PatientReviewedFields = z.infer<typeof patientBlockSchema>["reviewedFields"];
type CoverageReviewedFields = z.infer<typeof coverageBlockSchema>["reviewedFields"];
type ProviderReviewedFields = z.infer<typeof providerBlockSchema>["reviewedFields"];
type LocationReviewedFields = z.infer<typeof locationBlockSchema>["reviewedFields"];
type ReferralInformationReviewedFields = z.infer<typeof referralInformationBlockSchema>["reviewedFields"];
type DocumentInformationReviewedFields = z.infer<typeof documentInformationBlockSchema>["reviewedFields"];
type PriorAuthorizationReviewedFields = z.infer<typeof priorAuthorizationBlockSchema>["reviewedFields"];
type ProgressNoteReviewedFields = z.infer<typeof progressNoteBlockSchema>["reviewedFields"];
type ImagingResultsReviewedFields = z.infer<typeof imagingResultsBlockSchema>["reviewedFields"];
type LabResultsReviewedFields = z.infer<typeof labResultsBlockSchema>["reviewedFields"];
// Address field keys for nested address paths
type AddressFieldKey = keyof z.infer<typeof addressSchema>;

export type PatientFieldKey = keyof NonNullable<PatientReviewedFields>;
export type CoverageFieldKey = keyof NonNullable<CoverageReviewedFields>;
export type ProviderFieldKey = keyof NonNullable<ProviderReviewedFields>;
export type LocationFieldKey = keyof NonNullable<LocationReviewedFields>;
export type ReferralInformationFieldKey = keyof NonNullable<ReferralInformationReviewedFields>;
export type DocumentInformationFieldKey = keyof NonNullable<DocumentInformationReviewedFields>;
export type PriorAuthorizationFieldKey = keyof NonNullable<PriorAuthorizationReviewedFields>;
export type ProgressNoteFieldKey = keyof NonNullable<ProgressNoteReviewedFields>;
export type ImagingResultsFieldKey = keyof NonNullable<ImagingResultsReviewedFields>;
export type LabResultsFieldKey = keyof NonNullable<LabResultsReviewedFields>;

// BlockKey-based field paths
export type PatientFieldPath = `${BlockKey.PATIENT}.${PatientFieldKey}`;

export type PrimaryCoverageFieldPath = `${BlockKey.PRIMARY_COVERAGE}.${CoverageFieldKey}`;
export type SecondaryCoverageFieldPath = `${BlockKey.SECONDARY_COVERAGE}.${CoverageFieldKey}`;

export type OrderingProviderFieldPath = `${BlockKey.ORDERING_PROVIDER}.${ProviderFieldKey}`;
export type PrimaryCareProviderFieldPath = `${BlockKey.PRIMARY_CARE_PROVIDER}.${ProviderFieldKey}`;
export type ReferringProviderFieldPath = `${BlockKey.REFERRING_PROVIDER}.${ProviderFieldKey}`;
export type ReceivingProviderFieldPath = `${BlockKey.RECEIVING_PROVIDER}.${ProviderFieldKey}`;
export type ProviderFieldPath = `${BlockKey.PROVIDER}.${ProviderFieldKey}`; // Generic provider block

export type ReferringLocationFieldPath =
  | `${BlockKey.REFERRING_LOCATION}.${LocationFieldKey}`
  | `${BlockKey.REFERRING_LOCATION}.address.${AddressFieldKey}`;
export type ReceivingLocationFieldPath =
  | `${BlockKey.RECEIVING_LOCATION}.${LocationFieldKey}`
  | `${BlockKey.RECEIVING_LOCATION}.address.${AddressFieldKey}`;

export type ReferralInformationFieldPath = `${BlockKey.REFERRAL_INFORMATION}.${ReferralInformationFieldKey}`;
export type DocumentInformationFieldPath = `${BlockKey.DOCUMENT_INFORMATION}.${DocumentInformationFieldKey}`;
export type PriorAuthorizationFieldPath = `${BlockKey.PRIOR_AUTHORIZATION}.${PriorAuthorizationFieldKey}`;
export type ProgressNoteFieldPath = `${BlockKey.PROGRESS_NOTE}.${ProgressNoteFieldKey}`;
export type ImagingResultsFieldPath = `${BlockKey.IMAGING_RESULTS}.${ImagingResultsFieldKey}`;
export type LabResultsFieldPath = `${BlockKey.LAB_RESULTS}.${LabResultsFieldKey}`;

export type ReferralFieldPath =
  | PatientFieldPath
  | PrimaryCoverageFieldPath
  | SecondaryCoverageFieldPath
  | PrimaryCareProviderFieldPath
  | ReferringProviderFieldPath
  | ReceivingProviderFieldPath
  | ReferringLocationFieldPath
  | ReceivingLocationFieldPath
  | ReferralInformationFieldPath;

// Map of BlockKey to the corresponding reviewedFields shape.
export type BlockReviewedFieldsMap = {
  [K in BlockKey]: NonNullable<BlockMap[K]["reviewedFields"]>;
};

export type BlockReviewedFields<TBlockKey extends BlockKey> = BlockReviewedFieldsMap[TBlockKey];

// Named block interfaces keep editor hovers and downstream schema comparisons
// compact instead of expanding every field/metadata schema at each use site.
function namedBlockSchema<T>(schema: z.ZodType<T, unknown>): z.ZodType<T, unknown> {
  return schema;
}

// The authoritative association between a block key and its runtime schema.
export const BLOCK_SCHEMA_BY_KEY = {
  [BlockKey.PATIENT]: namedBlockSchema<PatientBlock>(patientBlockSchema),
  [BlockKey.PRIMARY_CARE_PROVIDER]: namedBlockSchema<ProviderBlock>(providerBlockSchema),
  [BlockKey.REFERRING_PROVIDER]: namedBlockSchema<ProviderBlock>(providerBlockSchema),
  [BlockKey.RECEIVING_PROVIDER]: namedBlockSchema<ProviderBlock>(providerBlockSchema),
  [BlockKey.ORDERING_PROVIDER]: namedBlockSchema<ProviderBlock>(providerBlockSchema),
  [BlockKey.PROVIDER]: namedBlockSchema<ProviderBlock>(providerBlockSchema),
  [BlockKey.REFERRING_LOCATION]: namedBlockSchema<LocationBlock>(locationBlockSchema),
  [BlockKey.RECEIVING_LOCATION]: namedBlockSchema<LocationBlock>(locationBlockSchema),
  [BlockKey.PRIMARY_COVERAGE]: namedBlockSchema<CoverageBlock>(coverageBlockSchema),
  [BlockKey.SECONDARY_COVERAGE]: namedBlockSchema<CoverageBlock>(coverageBlockSchema),
  [BlockKey.REFERRAL_INFORMATION]: namedBlockSchema<ReferralInformationBlock>(referralInformationBlockSchema),
  [BlockKey.DOCUMENT_INFORMATION]: namedBlockSchema<DocumentInformationBlock>(documentInformationBlockSchema),
  [BlockKey.PRIOR_AUTHORIZATION]: namedBlockSchema<PriorAuthorizationBlock>(priorAuthorizationBlockSchema),
  [BlockKey.PROGRESS_NOTE]: namedBlockSchema<ProgressNoteBlock>(progressNoteBlockSchema),
  [BlockKey.IMAGING_RESULTS]: namedBlockSchema<ImagingResultsBlock>(imagingResultsBlockSchema),
  [BlockKey.LAB_RESULTS]: namedBlockSchema<LabResultsBlock>(labResultsBlockSchema),
  [BlockKey.ADMIN]: namedBlockSchema<AdminBlock>(adminBlockSchema),
  [BlockKey.CONTINUITY_OF_CARE]: namedBlockSchema<ContinuityOfCareBlock>(continuityOfCareBlockSchema),
  [BlockKey.MEDICAL_HISTORY]: namedBlockSchema<MedicalHistoryBlock>(medicalHistoryBlockSchema),
  [BlockKey.MEDICAL_RECORD_REQUEST]: namedBlockSchema<MedicalRecordRequestBlock>(medicalRecordRequestBlockSchema),
  [BlockKey.NOTIFICATION]: namedBlockSchema<NotificationBlock>(notificationBlockSchema),
  [BlockKey.PHARMACY_RELATED]: namedBlockSchema<PharmacyRelatedBlock>(pharmacyRelatedBlockSchema),
  [BlockKey.EMERGENCY_SUMMARY]: namedBlockSchema<EmergencySummaryBlock>(emergencySummaryBlockSchema),
  [BlockKey.REFERRAL_RESPONSE]: namedBlockSchema<ReferralResponseBlock>(referralResponseBlockSchema),
  [BlockKey.CARE_PLAN]: namedBlockSchema<CarePlanBlock>(carePlanBlockSchema),
  [BlockKey.DME]: namedBlockSchema<DmeBlock>(dmeBlockSchema),
  [BlockKey.CLEARANCE_REQUEST]: namedBlockSchema<ClearanceRequestBlock>(clearanceRequestBlockSchema),
  [BlockKey.CONSENT]: namedBlockSchema<ConsentBlock>(consentBlockSchema),
  [BlockKey.FINANCIAL]: namedBlockSchema<FinancialBlock>(financialBlockSchema),
  [BlockKey.ID_CARD]: namedBlockSchema<IdCardBlock>(idCardBlockSchema),
  [BlockKey.INSURANCE]: namedBlockSchema<InsuranceBlock>(insuranceBlockSchema),
  [BlockKey.INTAKE]: namedBlockSchema<IntakeBlock>(intakeBlockSchema),
  [BlockKey.PATIENT_SUPPLEMENTAL]: namedBlockSchema<PatientSupplementalBlock>(patientSupplementalBlockSchema),
  [BlockKey.LEGAL]: namedBlockSchema<LegalBlock>(legalBlockSchema),
  [BlockKey.OPERATION_NOTE]: namedBlockSchema<OperationNoteBlock>(operationNoteBlockSchema),
  [BlockKey.PROCEDURE]: namedBlockSchema<ProcedureBlock>(procedureBlockSchema),
} satisfies Record<BlockKey, z.ZodTypeAny>;

export type BlockMap = {
  [K in BlockKey]: z.infer<(typeof BLOCK_SCHEMA_BY_KEY)[K]>;
};

export type BlockForKey<TBlockKey extends BlockKey> = BlockMap[TBlockKey];
